import { Route } from "@angular/router";

import { NotArchivedPlan, numericIdGuard, pmoEntity } from "../../core/guards";
import { Constants } from "../../shared/constants/constants";
import { PageMode } from "../../shared/constants/enums";
import {
  createAiAgentGuards,
  createAiTrackGuards,
  manageAiAgentGuards,
  manageAiMilestoneGuards,
  manageAiTaskGuards,
  manageAiTrackGuards,
  viewAiAgentGuards,
  viewAiMilestoneGuards,
  viewAiTaskGuards,
  viewAiTrackGuards,
} from "./_guards";

const AI_TRACKS_ROUTES: Route[] = [
  {
    path: "tracks",
    canActivate: [pmoEntity, ...viewAiTrackGuards],
    data: {
      breadcrumb: "ai_projects.tracks.title",
      title: "ai_projects.tracks.title",
    },
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./gov-tracks/gov-tracks-list/gov-tracks-list").then((e) => e.GovTracksListComponent),
        data: {
          trackType: Constants.GOV_TRACKS.AI,
        },
      },
      {
        path: "create",
        canActivate: [...createAiTrackGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./gov-tracks/gov-tracks-editor/gov-tracks-editor").then((e) => e.GovTracksEditorComponent),
        data: {
          breadcrumb: "ai_projects.tracks.create",
          title: "ai_projects.tracks.create",
          pageMode: PageMode.create,
          trackType: Constants.GOV_TRACKS.AI,
        },
      },
      {
        path: "edit/:id",
        canActivate: [...manageAiTrackGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./gov-tracks/gov-tracks-editor/gov-tracks-editor").then((e) => e.GovTracksEditorComponent),
        data: {
          breadcrumb: "ai_projects.tracks.edit",
          title: "ai_projects.tracks.edit",
          pageMode: PageMode.edit,
          trackType: Constants.GOV_TRACKS.AI,
        },
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./gov-tracks/gov-tracks-editor/gov-tracks-editor").then((e) => e.GovTracksEditorComponent),
        data: {
          breadcrumb: "ai_projects.tracks.single",
          title: "ai_projects.tracks.single",
          pageMode: PageMode.view,
          trackType: Constants.GOV_TRACKS.AI,
        },
      },
    ],
  },
];

const AI_MILESTONES_ROUTES: Route[] = [
  {
    path: "milestones",
    canActivate: [...viewAiMilestoneGuards],
    data: {
      breadcrumb: "ai_projects.milestones.title",
      title: "ai_projects.milestones.title",
    },
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./gov-milestones/gov-milestones-list/gov-milestones-list").then((e) => e.GovMilestonesListComponent),
        data: {
          milestoneType: Constants.GOV_MILESTONES.AI,
        },
      },
      {
        path: "edit/:id",
        canActivate: [...manageAiMilestoneGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./gov-milestones/gov-milestones-editor/gov-milestones-editor").then(
            (e) => e.GovMilestonesEditorComponent
          ),
        data: {
          breadcrumb: "ai_projects.milestones.edit",
          title: "ai_projects.milestones.edit",
          pageMode: PageMode.edit,
          milestoneType: Constants.GOV_MILESTONES.AI,
        },
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./gov-milestones/gov-milestones-editor/gov-milestones-editor").then(
            (e) => e.GovMilestonesEditorComponent
          ),
        data: {
          breadcrumb: "ai_projects.milestones.single",
          title: "ai_projects.milestones.single",
          pageMode: PageMode.view,
          milestoneType: Constants.GOV_MILESTONES.AI,
        },
      },
    ],
  },
];

const AI_TASKS_ROUTES: Route[] = [
  {
    path: "tasks",
    canActivate: [...viewAiTaskGuards],
    data: {
      breadcrumb: "ai_projects.tasks.title",
      title: "ai_projects.tasks.title",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("./gov-tasks/gov-tasks-list/gov-tasks-list").then((e) => e.GovTasksListComponent),
        data: {
          taskType: Constants.GOV_TASKS.AI,
        },
      },
      {
        path: "edit/:id",
        canActivate: [...manageAiTaskGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./gov-tasks/gov-tasks-editor/gov-tasks-editor").then((e) => e.GovTasksEditorComponent),
        data: {
          breadcrumb: "ai_projects.tasks.edit",
          title: "ai_projects.tasks.edit",
          pageMode: PageMode.edit,
          taskType: Constants.GOV_TASKS.AI,
        },
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./gov-tasks/gov-tasks-editor/gov-tasks-editor").then((e) => e.GovTasksEditorComponent),
        data: {
          breadcrumb: "ai_projects.tasks.single",
          title: "ai_projects.tasks.single",
          pageMode: PageMode.view,
          taskType: Constants.GOV_TASKS.AI,
        },
      },
    ],
  },
];

const AI_AGENTS_ROUTES: Route[] = [
  {
    path: "ai-agents",
    canActivate: [...viewAiAgentGuards],
    data: {
      breadcrumb: "ai_projects.agents.title",
      title: "ai_projects.agents.title",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("./ai-agents/ai-agents-list/ai-agents-list").then((e) => e.AiAgentsListComponent),
      },
      {
        path: "create",
        canActivate: [...createAiAgentGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./ai-agents/ai-agents-editor/ai-agents-editor").then((e) => e.AiAgentsEditorComponent),
        data: {
          breadcrumb: "ai_projects.agents.create",
          title: "ai_projects.agents.create",
          pageMode: PageMode.create,
        },
      },
      {
        path: "edit/:id",
        canActivate: [numericIdGuard("/console/ai-projects/ai-agents"), ...manageAiAgentGuards, NotArchivedPlan()],
        loadComponent: () =>
          import("./ai-agents/ai-agents-editor/ai-agents-editor").then((e) => e.AiAgentsEditorComponent),
        data: {
          breadcrumb: "ai_projects.agents.edit",
          title: "ai_projects.agents.edit",
          pageMode: PageMode.edit,
        },
      },
      {
        path: ":id",
        canActivate: [numericIdGuard("/console/ai-projects/ai-agents")],
        loadComponent: () =>
          import("./ai-agents/ai-agents-editor/ai-agents-editor").then((e) => e.AiAgentsEditorComponent),
        data: {
          breadcrumb: "ai_projects.agents.single",
          title: "ai_projects.agents.single",
          pageMode: PageMode.view,
        },
      },
    ],
  },
];

export const AI_PROJECTS_ROUTES: Route[] = [
  {
    path: "ai-projects",
    data: {
      breadcrumb: "ai_projects.title",
    },
    children: [
      {
        path: "",
        loadComponent: () => import("../../shared/components").then((e) => e.SubmenuComponent),
      },
      ...AI_TRACKS_ROUTES,
      ...AI_MILESTONES_ROUTES,
      ...AI_TASKS_ROUTES,
      ...AI_AGENTS_ROUTES,
    ],
  },
];
